import type { Fragment } from "../types.js";
import { colors } from "../../brand/theme.js";
import { escapeHtml } from "../composition.js";
import { tl } from "../timeline.js";

/**
 * HookText — large heading where each word fades+slides in with a 2-frame
 * stagger. The Remotion original used `spring()` per word; we approximate with
 * a tight power3.out ease (sub-second per word) at the same 2-frame ≈ 67ms
 * stagger.
 */
export function hookText(opts: {
  id: string;
  text: string;
  /** Seconds at which the first word starts entering. */
  startSec: number;
  size?: number;
  color?: string;
  highlight?: string;
  align?: "left" | "center";
  /** Per-word stagger in seconds. Default 2 frames @ 30fps = 0.0667s. */
  staggerSec?: number;
  /** Per-word enter duration in seconds. Default 0.6s. */
  wordDurationSec?: number;
}): Fragment {
  const {
    id,
    text,
    startSec,
    size = 110,
    color = colors.fg,
    highlight,
    align = "left",
    staggerSec = 2 / 30,
    wordDurationSec = 0.6,
  } = opts;

  const words = text.split(/(\s+)/);
  const renderedWords: string[] = [];
  let wordIdx = 0;
  words.forEach((w) => {
    if (/^\s+$/.test(w)) {
      renderedWords.push(w);
    } else {
      const isHl =
        highlight && w.toLowerCase().includes(highlight.toLowerCase());
      renderedWords.push(
        `<span class="word" data-w="${wordIdx}" style="display:inline-block;${isHl ? `color:${colors.accent};` : ""}">${escapeHtml(w)}</span>`,
      );
      wordIdx += 1;
    }
  });
  const wordCount = wordIdx;

  const html = `<h1 id="${id}" class="hook-text" style="margin:0;font-weight:900;font-size:${size}px;line-height:1.04;letter-spacing:-1.5px;color:${color};text-align:${align};text-wrap:balance;">${renderedWords.join(
    "",
  )}</h1>`;

  const tweens: string[] = [];
  for (let i = 0; i < wordCount; i++) {
    const at = Number((startSec + i * staggerSec).toFixed(4));
    tweens.push(
      tl.fromTo(
        `#${id} .word[data-w="${i}"]`,
        { opacity: 0, y: 28 },
        { opacity: 1, y: 0, duration: wordDurationSec, ease: "power3.out" },
        at,
      ),
    );
  }

  // Hide all words before startSec so they don't flash visible at t=0
  const init = `gsap.set(${JSON.stringify(`#${id} .word`)}, { opacity: 0, y: 28 });`;

  return { html, init, tweens };
}
